// frontend-repo/src/components/ChatSidebar.js
import { Github, MessageSquare, Moon, PanelLeft, Plus, Search, Sheet, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getAvatarUrl } from "../config/users";
import { UserAuth } from "../context/AuthContext";

function ChatSidebar({ conversations, currentConversationId, onSelectConversation, onNewConversation, backendContext, processContext }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(true);
  const [sheetsConnection, setSheetsConnection] = useState({ status: "checking" });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first, then system preference
    const stored = localStorage.getItem('chat-dark-mode');
    if (stored !== null) return stored === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const searchInputRef = useRef(null);
  const { user } = UserAuth();

  // Apply dark/light mode class to chat-app element
  useEffect(() => {
    const chatApp = document.querySelector('.chat-app');
    if (chatApp) {
      if (isDarkMode) {
        chatApp.classList.add('dark-mode');
        chatApp.classList.remove('light-mode');
      } else {
        chatApp.classList.remove('dark-mode');
        chatApp.classList.add('light-mode');
      }
    }
    localStorage.setItem('chat-dark-mode', isDarkMode);
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode(prev => !prev);

  // Source URLs
  const GITHUB_REPO = "https://github.com/Satori-AI-Research/Monika_backend";
  const GOOGLE_SHEET = "https://docs.google.com/spreadsheets/d/1zsoQZtyPQFAIvl7uhpDCSJ2Q0yyD7Hpopjnc9cu1TH8/edit?gid=778210656#gid=778210656";

  // Get context info - prefer live introspection data
  const gitCommit = processContext?.gitCommit?.substring(0, 7) || backendContext?.metadata?.gitCommit?.substring(0, 7);
  const gitBranch = processContext?.gitBranch || backendContext?.metadata?.gitBranch || 'main';
  const lastUpdated = processContext?.generatedAt
    ? new Date(processContext.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  // Helper to check if on mobile
  const isMobile = () => window.innerWidth <= 768;

  // Auto-collapse on mobile when selecting/creating chat
  const handleSelectConversation = (id) => {
    onSelectConversation(id);
    if (isMobile()) {
      setIsExpanded(false);
    }
  };

  const handleNewConversation = () => {
    onNewConversation();
    if (isMobile()) {
      setIsExpanded(false);
    }
  };

  // Open sidebar and focus search
  const handleSearchClick = () => {
    setIsExpanded(true);
    // Focus after sidebar expands
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  // Check Google Sheets connection on mount
  useEffect(() => {
    const host = process.env.REACT_APP_SERVER;
    const checkSheetsConnection = async () => {
      try {
        const response = await fetch(`${host}/api/connections/google-sheets`);
        const data = await response.json();
        setSheetsConnection(data);
      } catch (error) {
        setSheetsConnection({ status: "disconnected", error: error.message });
      }
    };
    checkSheetsConnection();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K: Focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        handleSearchClick();
      }
      // Cmd+N or Ctrl+N: New chat (only when not in input)
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        const activeElement = document.activeElement;
        const isInInput = activeElement?.tagName === 'INPUT' ||
                          activeElement?.tagName === 'TEXTAREA' ||
                          activeElement?.contentEditable === 'true';
        if (!isInInput) {
          e.preventDefault();
          handleNewConversation();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const avatarUrl = getAvatarUrl(user?.email, user?.photoURL) || "/default-avatar.jpg";

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group conversations by recency
  const getConversationGroups = () => {
    if (filteredConversations.length === 0) return [];

    const now = new Date();
    const today = [];
    const yesterday = [];
    const last7Days = [];
    const last30Days = [];
    const older = [];

    filteredConversations.forEach(conv => {
      const convDate = new Date(conv.createdAt);
      const daysDiff = Math.floor((now - convDate) / (1000 * 60 * 60 * 24));

      if (daysDiff === 0) {
        today.push(conv);
      } else if (daysDiff === 1) {
        yesterday.push(conv);
      } else if (daysDiff <= 7) {
        last7Days.push(conv);
      } else if (daysDiff <= 30) {
        last30Days.push(conv);
      } else {
        older.push(conv);
      }
    });

    const groups = [];
    if (today.length > 0) groups.push({ label: "Today", conversations: today });
    if (yesterday.length > 0) groups.push({ label: "Yesterday", conversations: yesterday });
    if (last7Days.length > 0) groups.push({ label: "Previous 7 days", conversations: last7Days });
    if (last30Days.length > 0) groups.push({ label: "Previous 30 days", conversations: last30Days });
    if (older.length > 0) groups.push({ label: "Older", conversations: older });

    return groups;
  };

  const conversationGroups = getConversationGroups();

  if (!isExpanded) {
    // Floating toolbar when collapsed
    return (
      <div className="sidebar-floating-toolbar">
        {/* Background blur */}
        <div className="sidebar-floating-bg"></div>

        {/* Expand button */}
        <button onClick={() => setIsExpanded(true)} className="sidebar-floating-button" title="Expand sidebar">
          <PanelLeft size={16} />
        </button>

        {/* Search button */}
        <button onClick={handleSearchClick} className="sidebar-floating-button" title="Search">
          <Search size={16} />
        </button>

        {/* New chat button */}
        <button onClick={handleNewConversation} className="sidebar-floating-button" title="New Chat">
          <Plus size={16} />
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Mobile backdrop - only show when sidebar is expanded */}
      {isExpanded && (
        <div
          className="sidebar-backdrop"
          onClick={() => setIsExpanded(false)}
        />
      )}
      <div className="sidebar-container">
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-header-content">
          <button onClick={() => setIsExpanded(false)} className="sidebar-collapse-button" title="Collapse sidebar">
            <PanelLeft size={18} />
          </button>
          <Link to="/" className="sidebar-title-link">
            <h1 className="sidebar-title">Monika</h1>
          </Link>
          <div className="sidebar-spacer" />
        </div>

        {/* New Chat Button */}
        <button onClick={handleNewConversation} className="sidebar-new-chat-button">
          <Plus size={16} stroke="white" />
          New Chat
        </button>
      </div>

      {/* Search Bar */}
      <div className="sidebar-search-container">
        <div className="sidebar-search-wrapper">
          <div className="sidebar-search-input-wrapper">
            <Search size={16} className="sidebar-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search your chats..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="sidebar-search-input"
            />
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="sidebar-conversations-container">
        {conversationGroups.length === 0 ? (
          <div className="sidebar-empty-state">
            <MessageSquare size={24} className="sidebar-empty-icon" />
            <p className="sidebar-empty-text">No conversations yet</p>
            <p className="sidebar-empty-subtext">Start a new chat to begin</p>
          </div>
        ) : (
          <div className="sidebar-conversations-list">
            {conversationGroups.map((group, groupIndex) => (
              <div key={group.label} className="sidebar-conversation-group">
                {/* Group Label */}
                <div className="sidebar-group-header">
                  <h3 className="sidebar-label">{group.label}</h3>
                </div>

                {/* Conversations */}
                <div className="sidebar-group-conversations">
                  {group.conversations.map(conv => {
                    const isActive = conv.id === currentConversationId;

                    return (
                      <button
                        key={conv.id}
                        onClick={() => handleSelectConversation(conv.id)}
                        className={`sidebar-conversation-button ${isActive ? "active" : ""}`}
                      >
                        <span className="sidebar-conversation-title">{conv.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connections Section */}
      <div className="sidebar-connections-container">
        <div className="sidebar-connections-header">
          <h3 className="sidebar-label">Sources</h3>
        </div>

        <div className="sidebar-connections-list">
          <a
            href={GITHUB_REPO}
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-connection-item clickable"
          >
            <Github size={16} className="sidebar-connection-icon" />
            <div className="sidebar-connection-info">
              <span className="sidebar-connection-name">GitHub</span>
              <span className="sidebar-connection-detail">
                {gitCommit ? `${gitBranch}@${gitCommit}` : 'Monika_backend'}
              </span>
            </div>
          </a>

          <a
            href={GOOGLE_SHEET}
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-connection-item clickable"
          >
            <Sheet size={16} className="sidebar-connection-icon" />
            <div className="sidebar-connection-info">
              <span className="sidebar-connection-name">Process Spec</span>
              {sheetsConnection.status === "checking" ? (
                <span className="sidebar-connection-detail">Checking...</span>
              ) : sheetsConnection.status === "connected" ? (
                <span className="sidebar-connection-detail">
                  {lastUpdated ? `Updated ${lastUpdated}` : 'Connected'}
                </span>
              ) : (
                <span className="sidebar-connection-detail error">No Connection</span>
              )}
            </div>
          </a>
        </div>
      </div>

      {/* User Section */}
      <div className="sidebar-user-section">
        <div className="sidebar-user-row">
          {user && (
            <div className="sidebar-user-info">
              <img src={avatarUrl} alt="User Avatar" className="sidebar-user-avatar" referrerPolicy="no-referrer" />
              <div className="sidebar-user-details">
                <p className="sidebar-user-name">{user.displayName || "User"}</p>
              </div>
            </div>
          )}
          <button
            onClick={toggleDarkMode}
            className="sidebar-theme-toggle"
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

export default ChatSidebar;
